import { NavLink } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CalendarCheck,
  CalendarClock,
  CheckCheck,
  CheckSquare,
  ClipboardList,
  BarChart3,
  Hand,
  History,
  Home,
  Layers,
  ListTodo,
  ScanLine,
  Settings,
  UserRound,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export type BottomNavItem = {
  id: string;
  label: string;
  route: string;
  icon: string;
};

/** Keys match design Lucide names (EXE-01 / MOB-01 / Field registry). */
const iconMap: Record<string, LucideIcon> = {
  'clipboard-list': ClipboardList,
  'calendar-check': CalendarCheck,
  'calendar-clock': CalendarClock,
  'scan-line': ScanLine,
  warehouse: Warehouse,
  bell: Bell,
  user: UserRound,
  'user-round': UserRound,
  home: Home,
  layers: Layers,
  history: History,
  settings: Settings,
  palm: Hand,
  'list-todo': ListTodo,
  'check-square': CheckSquare,
  'check-check': CheckCheck,
  'alert-triangle': AlertTriangle,
  'triangle-alert': AlertTriangle,
  'file-bar-chart': BarChart3,
  'bar-chart-3': BarChart3,
};

export type BottomNavigationProps = {
  items: BottomNavItem[];
  activeId: string | null;
  className?: string;
};

/**
 * Field / EXE bottom nav: icon 24px, stroke 2 outline.
 * Active = primary color + accent pip (no fill, no 2.5 stroke).
 */
export function BottomNavigation({ items, activeId, className }: BottomNavigationProps) {
  const colsClass =
    items.length === 5
      ? 'grid-cols-5'
      : items.length === 4
        ? 'grid-cols-4'
        : items.length === 3
          ? 'grid-cols-3'
          : 'grid-flow-col auto-cols-fr';

  return (
    <nav
      aria-label="Điều hướng chính"
      className={cn(
        'keyboard-lift fixed inset-x-0 bottom-[var(--keyboard-inset,0px)] z-[var(--z-sticky)] border-t border-border bg-surface-raised',
        'pb-[env(safe-area-inset-bottom)]',
        className,
      )}
    >
      <ul
        className={cn(
          'm-0 grid list-none items-stretch gap-0 p-0',
          'h-[var(--layout-bottom-nav-height)]',
          colsClass,
        )}
      >
        {items.map((item) => {
          const Icon = iconMap[item.icon] ?? Home;
          const active = activeId === item.id;
          return (
            <li key={item.id} className="min-w-0">
              <NavLink
                to={item.route}
                end={
                  item.route === '/executive' ||
                  item.route === '/mobile' ||
                  item.route === '/mobile/today'
                }
                aria-current={active ? 'page' : undefined}
                data-active={active ? 'true' : 'false'}
                className={cn(
                  'relative flex h-full flex-col items-center justify-center gap-0.5 px-1',
                  'text-[10px] leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-focus-ring)]',
                  active
                    ? 'font-bold text-primary'
                    : 'font-semibold text-text-muted hover:text-text-secondary',
                )}
              >
                <Icon
                  className="size-6 shrink-0"
                  strokeWidth={2}
                  absoluteStrokeWidth
                  fill="none"
                  aria-hidden
                />
                <span className="max-w-full truncate">{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
